// ./src/components/features/Contacts/ContactsFilterBar/ContactsFilterBar.tsx
//
// Filter controls for the Contacts list: by status (active / disabled) and by
// upcoming birthday. Fully controlled — the page owns the value and maps it to a
// PersonFilter.

import React from "react";
import { Group, SegmentedControl, Input, Button, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { PERSON_UPCOMING_BIRTHDAY_DAYS } from "@/utils/api";
import { AppIcon, TagsSelectorInput } from "@/components/generic";
import { useAppEnvironment } from "@/contexts";
import { ContactTagFilter } from "../ContactTagFilter/ContactTagFilter";

// ============================================
// Types
// ============================================

export type ContactStatusFilter = "active" | "disabled";

export interface ContactsFilterValue {
  status: ContactStatusFilter | null;
  upcomingBirthday: boolean;
  /** Only contacts carrying ANY of these tags. */
  tagIds: string[];
}

export interface ContactsFilterBarProps {
  value: ContactsFilterValue;
  onChange: (value: ContactsFilterValue) => void;
  disabled?: boolean;
  /** Show the inline Clear button. Turn off inside a FilterDrawer. */
  showClear?: boolean;
}

export const EMPTY_CONTACTS_FILTER: ContactsFilterValue = {
  status: null,
  upcomingBirthday: false,
  tagIds: [],
};

const isFilterActive = (value: ContactsFilterValue): boolean =>
  Boolean(value.status || value.upcomingBirthday || value.tagIds.length > 0);

const STATUS_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };
const BIRTHDAY_STYLE: React.CSSProperties = { flex: "1 1 220px", minWidth: 200 };
const TAGS_STYLE: React.CSSProperties = { flex: "1 1 240px", minWidth: 220 };

const ANY = "any";
const UPCOMING = "upcoming";

// ============================================
// Component
// ============================================

const ContactsFilterBarComponent: React.FC<ContactsFilterBarProps> = ({
  value,
  onChange,
  disabled = false,
  showClear = true,
}) => {
  const { t } = useTranslation(["contacts", "common"]);
  const { isMobile } = useAppEnvironment();

  const statusOptions = [
    { value: ANY, label: t("contacts:filters.status.any", { defaultValue: "Any" }) },
    { value: "active", label: t("contacts:filters.status.active", { defaultValue: "Active" }) },
    { value: "disabled", label: t("contacts:filters.status.disabled", { defaultValue: "Disabled" }) },
  ];

  const birthdayOptions = [
    { value: ANY, label: t("contacts:filters.birthday.any", { defaultValue: "All" }) },
    {
      value: UPCOMING,
      label: t("contacts:filters.birthday.upcoming", {
        defaultValue: "Upcoming ({{days}}d)",
        days: PERSON_UPCOMING_BIRTHDAY_DAYS,
      }),
    },
  ];

  const active = isFilterActive(value);

  return (
    <Group gap="sm" align="flex-end" wrap="wrap">
      <Box style={STATUS_STYLE}>
        <Input.Wrapper label={t("contacts:filters.status.label", { defaultValue: "Status" })}>
          <SegmentedControl
            data={statusOptions}
            value={value.status ?? ANY}
            onChange={(next) =>
              onChange({ ...value, status: next === ANY ? null : (next as ContactStatusFilter) })
            }
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      <Box style={BIRTHDAY_STYLE}>
        <Input.Wrapper label={t("contacts:filters.birthday.label", { defaultValue: "Birthday" })}>
          <SegmentedControl
            data={birthdayOptions}
            value={value.upcomingBirthday ? UPCOMING : ANY}
            onChange={(next) => onChange({ ...value, upcomingBirthday: next === UPCOMING })}
            disabled={disabled}
            fullWidth
            size="md"
            mt={4}
          />
        </Input.Wrapper>
      </Box>

      {/* Tags stay on this single row. Desktop uses a compact overflow control
          (pills + "+N", popover checklist); phones keep the full-screen modal
          picker (a popover inside the filter drawer would fight its focus trap). */}
      <Box style={TAGS_STYLE}>
        {isMobile ? (
          <TagsSelectorInput
            label={t("contacts:filters.tags.label", { defaultValue: "Tags" })}
            placeholder={t("contacts:filters.tags.placeholder", { defaultValue: "Any tag" })}
            value={value.tagIds}
            onChange={(_name, tagIds) => onChange({ ...value, tagIds })}
            disabled={disabled}
            allowCreate={false}
            maxTags={20}
            size="md"
          />
        ) : (
          <ContactTagFilter
            label={t("contacts:filters.tags.label", { defaultValue: "Tags" })}
            value={value.tagIds}
            onChange={(tagIds) => onChange({ ...value, tagIds })}
            disabled={disabled}
            size="md"
          />
        )}
      </Box>

      {active && showClear && (
        <Button
          variant="subtle"
          color="gray"
          size="md"
          onClick={() => onChange(EMPTY_CONTACTS_FILTER)}
          disabled={disabled}
          leftSection={<AppIcon iconName="X" size={16} />}
        >
          {t("common:buttons.clear", { defaultValue: "Clear" })}
        </Button>
      )}
    </Group>
  );
};

export const ContactsFilterBar = React.memo(ContactsFilterBarComponent);
